import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, X, Loader2 } from 'lucide-react';
import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from '@scio3/ui';
import { isEnterKey } from '@/lib/keyboard';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ComboboxItem {
  id: string;
  label: string;
  /** Secondary text shown on the right side (e.g. folder path) */
  secondaryLabel?: string;
}

export interface MultiSelectComboboxProps {
  /** Items to display in the dropdown (consumer controls this based on search state) */
  items: ComboboxItem[];
  /** Currently selected items (full objects so chips render even when not in search results) */
  selectedItems: ComboboxItem[];
  /** Called when an item is toggled on/off */
  onToggle: (item: ComboboxItem) => void;
  /** Current search query */
  searchQuery: string;
  /** Called when search text changes (consumer debounces and switches data source) */
  onSearchChange: (query: string) => void;
  /** Loading state for data fetching */
  loading?: boolean;
  /** Placeholder for the trigger button */
  placeholder?: string;
  /** Placeholder for the search input */
  searchPlaceholder?: string;
  /** Empty state message */
  emptyMessage?: string;
  /** Whether the combobox is disabled */
  disabled?: boolean;
  /** Additional className for the trigger */
  className?: string;
  /** Hide already-selected items from the dropdown (default true — scio1 parity) */
  hideSelected?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MultiSelectCombobox({
  items,
  selectedItems,
  onToggle,
  searchQuery,
  onSearchChange,
  loading = false,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  hideSelected = true,
  disabled = false,
  className,
}: MultiSelectComboboxProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [triggerWidth, setTriggerWidth] = useState<number>(0);

  // Measure trigger width so popover matches
  useEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [open]);

  const selectedIds = useMemo(() => new Set(selectedItems.map((item) => item.id)), [selectedItems]);

  const handleSelect = useCallback(
    (itemId: string) => {
      const existing = selectedItems.find((s) => s.id === itemId);
      if (existing) {
        onToggle(existing);
      } else {
        const item = items.find((i) => i.id === itemId);
        if (item) onToggle(item);
      }
    },
    [items, selectedItems, onToggle],
  );

  const handleRemove = useCallback(
    (e: React.MouseEvent, item: ComboboxItem) => {
      e.stopPropagation();
      onToggle(item);
    },
    [onToggle],
  );

  // When hideSelected is true, filter out already-selected items from dropdown
  // (scio1 parity: selected items appear only as chips, not in the choices list).
  // When false, merge selected items not in current results so they stay visible.
  const displayItems = useMemo(() => {
    if (hideSelected) {
      return items.filter((i) => !selectedIds.has(i.id));
    }
    const itemIds = new Set(items.map((i) => i.id));
    const missingSelected = selectedItems.filter((s) => !itemIds.has(s.id));
    return [...missingSelected, ...items];
  }, [items, selectedItems, selectedIds, hideSelected]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="trigger"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'h-auto min-h-9 w-full justify-between overflow-hidden whitespace-normal rounded-md',
            selectedItems.length > 0 && 'py-1.5',
            className,
          )}
        >
          <div className="flex min-w-0 flex-1 flex-wrap gap-1 overflow-hidden">
            {selectedItems.length === 0 && (
              <span className="text-muted-foreground">{placeholder ?? t('Select...')}</span>
            )}
            {selectedItems.map((item) => (
              <Badge
                key={item.id}
                variant="outline"
                className={cn(
                  'min-w-0 max-w-full gap-1 pr-1',
                  disabled && 'border-muted-foreground/20 bg-transparent text-muted-foreground',
                )}
              >
                <span className="truncate">{item.label}</span>
                <span
                  role={disabled ? undefined : 'button'}
                  tabIndex={disabled ? -1 : 0}
                  aria-label={t('Remove {{name}}', { name: item.label })}
                  aria-disabled={disabled || undefined}
                  className={cn(
                    'shrink-0 rounded-sm',
                    disabled
                      ? 'pointer-events-none opacity-40'
                      : 'cursor-pointer opacity-70 hover:opacity-100',
                  )}
                  onClick={disabled ? undefined : (e) => handleRemove(e, item)}
                  onKeyDown={
                    disabled
                      ? undefined
                      : (e) => {
                          if (isEnterKey(e) || e.key === ' ') {
                            e.preventDefault();
                            onToggle(item);
                          }
                        }
                  }
                >
                  <X className="h-3 w-3" />
                </span>
              </Badge>
            ))}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="p-0"
        style={{ width: triggerWidth > 0 ? triggerWidth : undefined }}
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder ?? t('Search...')}
            value={searchQuery}
            onValueChange={onSearchChange}
          />
          <CommandList>
            {loading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
            {!loading && displayItems.length === 0 && (
              <CommandEmpty>{emptyMessage ?? t('No results found.')}</CommandEmpty>
            )}
            {!loading && displayItems.length > 0 && (
              <CommandGroup>
                {displayItems.map((item) => {
                  const isSelected = selectedIds.has(item.id);
                  return (
                    <CommandItem
                      key={item.id}
                      value={item.id}
                      onSelect={() => handleSelect(item.id)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4 shrink-0',
                          isSelected ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.secondaryLabel && (
                        <span
                          className="ml-2 max-w-[40%] shrink-0 truncate text-xs text-muted-foreground"
                          title={item.secondaryLabel}
                        >
                          {item.secondaryLabel}
                        </span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
